/**
 * Command Center + ML — create and run one geo test.
 * Stages: Plan → Design → Test plan → In flight (mid-flight reads) → Decide.
 *
 * Both methods run on real Adrsta engines. Synthetic control: history upload
 * → DTW candidate markets → GeoLift power analysis / market selection →
 * GeoLift reads during the flight. RCT: history upload → Adrsta RCT split search → test vs
 * control readout. Designs and readouts are background jobs on the server
 * (command_center_jobs), so the user can leave and come back; the page polls
 * while one runs. Guardrails come from the tenant's rules, evaluated by the backend.
 */

import React, { useEffect, useRef, useState } from "react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import {
  Badge, BauInput, BudgetInput, CcStyles, ChangeField, CollapseSection, DollarInput, DurationsInput, StatSigInput, ReadoutChart, ChecksList, ChecksSummary, DecisionField, ErrorBanner, JobProgress, Spinner, StageBar, StartDateField, WeeksInput, errMsg,
  printElement, useChecks, useSavedTest,
} from "./components";
import { LibraryEntry, RegressionType, ccApi, ccBase } from "./api";
import RctDesignResults from "./RctDesignResults";
import ViewTestAndControlMarket from "../measurement/geo-lift/components/ViewTestAndControlMarket";
import ViewResults from "../measurement/geo-lift/components/ViewResults";
import { addDays, decisionText, defaultStart, dollars, fmtDayYear, marketName, migrateFlow, money, parseDay, todayIso } from "./common";
import { ML_DEFAULT, ML_STAGES, MlState, computeMl, mlResult, mlStatus } from "./mlModel";

const BASE = ccBase("ml");

export default function MlTestFlow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useSavedTest<MlState>({ mode: "ml", testId: id, defaults: { ...ML_DEFAULT, startDate: defaultStart() }, statusOf: mlStatus, migrate: migrateFlow });
  const { state: s, update, engine, live, loading } = t;
  const d = computeMl(s, engine);
  const { forStage, blockers } = useChecks(id, t.version);
  const preInput = useRef<HTMLInputElement>(null);
  const postInput = useRef<HTMLInputElement>(null);
  const planRef = useRef<HTMLElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [statSigBad, setStatSigBad] = useState(false);
  const [library, setLibrary] = useState<LibraryEntry[]>([]);

  useEffect(() => { ccApi.library("ml").then(setLibrary).catch(() => {}); }, []);

  // Poll while a design or readout job runs on the server.
  const powerRunning = d.power?.status === "running";
  const rctRunning = d.rct?.status === "running";
  const readoutRunning = engine.readout?.status === "running";
  const anyRunning = powerRunning || rctRunning || readoutRunning;
  useEffect(() => {
    if (!id || !anyRunning) return;
    const timer = setInterval(() => {
      (powerRunning ? ccApi.pollPower(id) : ccApi.getTest(id)).then(t.applyTest).catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [id, anyRunning, powerRunning, t.applyTest]);

  // The RCT engine picks the duration; the flight follows it.
  const rctWeeks = d.rctDesign?.weeks;
  useEffect(() => {
    if (!d.sc && rctWeeks && s.flightWeeks !== rctWeeks) update({ flightWeeks: rctWeeks });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rctWeeks, d.sc]);

  const go = (n: number) => update(p => ({ stage: n, maxStage: Math.max(p.maxStage, n) }));
  const canCreate = !!(s.name.trim() && s.adv.trim());
  const status = mlStatus(s);

  const onCreate = async () => {
    if (!canCreate) return;
    try {
      const created = await t.create({ ...s, stage: 1, maxStage: 1 });
      navigate(`${BASE}/tests/${created.id}`, { replace: true });
    } catch (err) {
      t.setError(`Could not create the test: ${errMsg(err)}`);
    }
  };

  const run = async (label: string, fn: () => Promise<any>) => {
    setBusy(label); setProgress(0);
    try { await fn(); } catch (err) { t.setError(errMsg(err)); } finally { setBusy(null); }
  };

  const uploadHistory = (file: File | null) => id && run("history", async () => {
    t.applyTest(await ccApi.uploadHistory(id, file, setProgress));
    update({ designId: null, anchors: [], scMarkets: undefined });
  });
  // Synthetic control: the markets where ads will run, pre-filled with the suggested
  // (DTW cluster centre) markets like the GeoLift study's "Select Markets".
  const marketOptions = Object.entries(d.names || {}).map(([key, label]) => ({ value: key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const scMarkets = s.scMarkets ?? (d.hist?.candidates || []).map(c => c.key);
  const ranMarkets = d.power?.markets;
  const marketsChanged = !!ranMarkets && (ranMarkets.length !== scMarkets.length || scMarkets.some(m => !ranMarkets.includes(m)));
  // BAU spend, typed monthly like GeoLift's "Average Monthly BAU Media Spent". Both engines size
  // the test investment from it. Not guessed from an adspend column: that column often holds a
  // past campaign's spend, not BAU.
  const toMonthly = (wk: number) => Math.round((wk * 52) / 12 * 10) / 10;
  const bauMonthlyK = s.rctBauMonthlyK ?? (s.rctBauK != null ? toMonthly(s.rctBauK) : undefined);
  // Synthetic control: GeoLift's "Campaign Start Date". The history before it trains the
  // synthetic controls; the test window runs from it to the end of the file.
  const daily = d.facts?.granularity === "daily";
  const scStart = s.scCampaignStart;
  const histEnd = parseDay(d.facts?.date_to || "");
  const scStartDay = parseDay(scStart || "");
  const scWindow = scStartDay && histEnd
    ? Math.round((histEnd.getTime() - scStartDay.getTime()) / 86400000 / (daily ? 1 : 7)) + 1 : null;
  const scWeeks = scWindow == null ? s.maxWeeks : daily ? Math.max(1, Math.round(scWindow / 7)) : scWindow;
  const pastCampaign = d.facts?.campaign_spend;
  // The picker opens on the history (it ends months ago, so today's month would be all greyed out):
  // the chosen date, else the past campaign's start, else the default window start.
  const histFrom = d.facts?.date_from ? dayjs(d.facts.date_from) : null;
  const windowStart = d.facts?.date_to ? dayjs(d.facts.date_to).subtract(daily ? s.maxWeeks * 7 - 1 : (s.maxWeeks - 1) * 7, "day") : undefined;
  const scPickerOpen = scStart ? dayjs(scStart) : pastCampaign ? dayjs(pastCampaign.from) : windowStart;
  // Weekly history: a picked day becomes the week in the file that contains it.
  const snapToHistory = (v: dayjs.Dayjs) => {
    if (daily || !histFrom) return v.format("YYYY-MM-DD");
    return histFrom.add(Math.floor(v.diff(histFrom, "day") / 7) * 7, "day").format("YYYY-MM-DD");
  };
  // GeoLift's "Regression Type": Ridge ASCM unless SCM is picked.
  const regressionType: RegressionType = s.scRegressionType ?? "Ridge ASCM";
  // GeoLift's "Statistical Significance Threshold %" (50-100, default 80).
  const statSig = s.scStatSig ?? d.power?.stat_sig_threshold ?? 80;
  const ranStart = d.power?.campaign_start;
  const inputsChanged = !!d.power && d.power.status !== "running" && (
    (!!scStart && !!ranStart && ranStart !== scStart)
    || (d.power.bau_monthly_k != null && bauMonthlyK != null && d.power.bau_monthly_k !== bauMonthlyK)
    || (d.power.regression_type ?? "SCM") !== regressionType
    || (d.power.stat_sig_threshold ?? 80) !== statSig);
  const startPower = () => id && scMarkets.length && bauMonthlyK && !statSigBad && run("power", async () => {
    t.applyTest(await ccApi.startPower(id, { markets: scMarkets, campaign_start: scStart || undefined, bau_monthly_k: bauMonthlyK, regression_type: regressionType, stat_sig_threshold: statSig }));
    update({ scMarkets, designId: null, rctBauMonthlyK: bauMonthlyK });
  });
  // RCT: the RCT Experiment's Setup inputs, sent exactly as typed (only 50/50 for now).
  const rctDurations = s.rctDurations ?? d.rct?.durations ?? [];
  const rctWeeklyCap = s.rctWeeklyCap ?? d.rct?.weekly_cap ?? (s.budget ? s.budget * 1000 : undefined);
  const rctWeeklySpend = s.rctWeeklySpend ?? d.rct?.total_weekly_spend ?? (s.rctBauK ? Math.round(s.rctBauK * 1000 * 100) / 100 : undefined);
  const rctReady = rctDurations.length > 0 && !!rctWeeklyCap && !!rctWeeklySpend;
  const startRct = () => id && rctReady && run("rct", async () => {
    t.applyTest(await ccApi.startRct(id, { split: 50, durations: rctDurations, weekly_cap: rctWeeklyCap!, total_weekly_spend: rctWeeklySpend! }));
    update({ split: 50, rctDurations, rctWeeklyCap, rctWeeklySpend });
  });
  const startReadout = (file: File | null) => id && run("readout", async () => {
    t.applyTest(await ccApi.startReadout(id, file, setProgress));
  });
  // Like the GeoLift study, the suggested markets come from the history before the
  // campaign start date, so they are re-clustered when the date changes.
  const suggestedFor = d.hist?.candidates_start;
  const suggestTried = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !d.sc || !scStart || !d.hist?.job_config_id || engine.sample || powerRunning) return;
    if (suggestedFor === scStart || suggestTried.current === scStart) return;
    suggestTried.current = scStart;
    ccApi.suggestMarkets(id, scStart).then(t.applyTest).catch(err => t.setError(errMsg(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, d.sc, scStart, suggestedFor, d.hist?.job_config_id, engine.sample, powerRunning]);

  if (loading) return <div className="cc-page"><CcStyles /><div className="cc-skel" style={{ height: 320 }} /></div>;

  const rev = d.rev;
  const sc = d.sc;
  const title = s.stage === 0 ? "New geo test" : `${d.adv} · ${s.name.trim() || "New geo test"}`;
  const planBlocks = blockers("plan");
  const designBlocks = [...blockers("data"), ...blockers("design")];
  const designNextOk = d.designDone && !designBlocks.length;
  const avg = (v: number[]) => (v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0);
  const question = decisionText(s.decision) || "Did the test work?";
  // Per-market power analysis rows for the markets in the plan.
  const planRows = d.design && sc ? d.designs.filter(r => d.design!.markets.includes(r.markets[0])) : [];
  // Only the plan card goes into the PDF (Save as PDF in the print dialog).
  const downloadPlan = () => printElement(planRef.current, `Test plan - ${d.adv} - ${s.name.trim() || "geo test"}`);
  const winTexts: Record<string, string> = {
    scale: `${d.adv} increases the budget for this channel.`, roll: `${d.adv} rolls the campaign out to all markets.`, keep: `${d.adv} keeps current spend, now with proof it works.`,
  };
  const decDefs = d.met
    ? [["scale", "Increase the budget (as agreed in the plan)"], ["keep", "Keep current spend"], ["retest", "Retest in another season or market set"]]
    : [["keep", "Keep spend as is"], ["retest", "Retest with more weeks or budget"], ["stop", "Stop or cut this spend"]];
  const phases: [string, Date | null, string][] = [
    ["Scheduled", d.start ? addDays(d.start, -7) : null, "Plan approved"],
    ["Running", d.start, "Ads live in test markets"],
    ["Ended", d.end, "Ads stop; last read is final"],
  ];
  const pill = (on: boolean) => `cc-pill${on ? " on" : ""}`;
  // Number formats of the RCT Experiment's Results screen.
  const rx = d.rctDesign?.exact;
  const num2 = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const usd = (n: number) => `${Math.round(n).toLocaleString()}`;
  const opt = (on: boolean) => `cc-opt${on ? " on" : ""}`;
  const nm = (k: string) => marketName(k, d.names);

  // Readout chart from the GeoLift lift plot.
  const chart = d.ro?.chart;
  const hasChart = !!(chart?.t?.length && chart.c?.length === chart.t.length);

  return (
    <div className="cc-page">
      <CcStyles />

      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div className="cc-crumb"><button onClick={() => navigate(BASE)}>Command Center + ML</button> / {title}</div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <h1 className="cc-h1">{title}</h1>
            <Badge tone={status.tone}>{status.label}</Badge>
            <span className="cc-chip" style={{ color: "var(--blue)", background: "var(--blue-subtle)" }}>Command Center + ML · Adrsta stats</span>
            {s.stage > 0 && <span className="cc-chip">{d.methodShort}</span>}
            {s.stage > 0 && <span className="cc-chip">{d.kpiShort}</span>}
            {engine.sample && <span className="cc-chip" title="Seeded sample test: its designs are a stored snapshot">Sample</span>}
          </div>
        </div>
        {t.saving && <span className="cc-help">Saving…</span>}
      </div>

      <ErrorBanner msg={t.error} onClose={() => t.setError(null)} />

      {s.stage > 0 && <StageBar labels={ML_STAGES} stage={s.stage} maxStage={s.maxStage} onGo={n => update({ stage: n })} />}

      {/* ── 0 · CREATE ── */}
      {s.stage === 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div className="cc-card" style={{ flex: "1 1 560px", minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>Create a new geo test</span>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="cc-fl">Test name</span>
              <input className="cc-input" value={s.name} onChange={e => t.setState(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Q1 CTV launch" />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="cc-fl">Advertiser / brand</span>
              <input className="cc-input" value={s.adv} onChange={e => t.setState(p => ({ ...p, adv: e.target.value }))} placeholder="e.g. Acme Home" />
            </label>
            <span className="cc-fl" style={{ marginTop: 4 }}>What does the KPI measure?</span>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className={opt(rev)} onClick={() => t.setState(p => ({ ...p, kpi: "rev" }))}>
                <span style={{ fontWeight: 600 }}>Revenue ($)</span>
                <span className="cc-help">Sales in dollars. Results show lift, incremental $ and iROAS.</span>
              </button>
              <button className={opt(!rev)} onClick={() => t.setState(p => ({ ...p, kpi: "cnt" }))}>
                <span style={{ fontWeight: 600 }}>A count</span>
                <span className="cc-help">Orders, sign-ups, store visits, installs. Results show lift and cost per incremental; no $.</span>
              </button>
            </div>
            <span className="cc-fl" style={{ marginTop: 4 }}>Which method?</span>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className={opt(sc)} onClick={() => t.setState(p => ({ ...p, method: "sc" }))}>
                <span style={{ fontWeight: 600 }}>Synthetic control (GeoLift)</span>
                <span className="cc-help">Ads run in a few test markets chosen by the GeoLift engine. The other markets are blended into a “twin” that shows what would have happened without ads.</span>
              </button>
              <button className={opt(!sc)} onClick={() => t.setState(p => ({ ...p, method: "rct" }))}>
                <span style={{ fontWeight: 600 }}>Randomized split (RCT)</span>
                <span className="cc-help">Markets are randomly split 50/50 into test and control by the Adrsta RCT engine. Needs many markets and more budget.</span>
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", paddingTop: 4 }}>
              <span className="cc-help">{canCreate ? "Ready to create." : "Enter a test name and advertiser."}</span>
              <button className={`cc-btn${canCreate ? " primary" : ""}`} disabled={!canCreate} onClick={onCreate}>Create test →</button>
            </div>
          </div>
          <div style={{ flex: "1 1 280px", maxWidth: 340, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Which method should I pick?</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13, lineHeight: 1.5, color: "var(--text-secondary)" }}>
                <div><b style={{ color: "var(--text-primary)" }}>Synthetic control</b> when you can only advertise in a few markets, have a smaller budget, or the client chose the markets.</div>
                <div><b style={{ color: "var(--text-primary)" }}>RCT</b> when you can run in many markets (roughly 100+ DMAs) and want the strongest proof; random assignment removes picking bias.</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 1 · PLAN ── */}
      {s.stage === 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 480px", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            <DecisionField value={s.decision} onChange={v => update({ decision: v })} />
            <StartDateField value={s.startDate} onChange={v => update({ startDate: v })} />
            <div className="cc-card">
              <div className="cc-qh"><span>Longest the advertiser can run the test</span></div>
              <WeeksInput value={s.maxWeeks} onChange={w => update({ maxWeeks: w, flightWeeks: w })} ariaLabel="Longest the advertiser can run the test, in weeks" />
              <span className="cc-help">{sc ? "The power analysis tests a flight this long." : "The flight follows the duration the RCT engine picks."}</span>
            </div>
            <div className="cc-card">
              <div className="cc-qh"><span>Test budget per week (test markets only)</span></div>
              <BudgetInput value={s.budget} onChange={b => update({ budget: b })} ariaLabel="Test budget per week in thousands of dollars" />
              <span className="cc-help">
                The extra media spend for the test, on top of business as usual (BAU).{" "}
                {sc ? "Designs that need more per week than this are marked over budget." : "Pre-fills the RCT's weekly incremental spend cap."}
              </span>
            </div>
            <ChangeField change={s.change} note={s.changeNote} onChange={p => update(p)} />
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Guardrail rules for the plan</span>
              <ChecksList checks={forStage("plan")} compact />
            </div>
          </div>
          <div style={{ flex: "1 1 260px", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12 }}>
            <ChecksSummary checks={forStage("plan")} />
            <div className="cc-card">
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>Benchmarks</span>
              {library.length ? (
                <>
                  <div style={{ display: "flex", gap: 18 }}>
                    <div><div className="cc-lbl">Avg lift</div><div className="cc-val">+{avg(library.map(e => e.lift)).toFixed(1)}%</div></div>
                    <div><div className="cc-lbl">Met bar</div><div className="cc-val">{library.filter(e => e.met).length}/{library.length}</div></div>
                  </div>
                  <span className="cc-help">From {library.length} past test{library.length > 1 ? "s" : ""} in the Learning library.</span>
                </>
              ) : <span className="cc-help">No tests in the Learning library yet.</span>}
            </div>
            <button className={`cc-btn${planBlocks.length || !decisionText(s.decision) ? "" : " primary"}`} disabled={!!planBlocks.length || !decisionText(s.decision)} onClick={() => go(2)}>
              {!decisionText(s.decision) ? "Enter the decision to continue"
                : planBlocks.length ? `Clear ${planBlocks.length} blocker${planBlocks.length > 1 ? "s" : ""} to continue` : "Continue to Design →"}
            </button>
          </div>
        </div>
      )}

      {/* ── 2 · DESIGN ── */}
      {s.stage === 2 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 520px", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="cc-card">
              <div className="cc-qh"><span>1 · Historical data</span><Badge tone={d.hist ? "green" : "grey"}>{d.hist ? "Uploaded" : "Needed"}</Badge></div>
              <span className="cc-help">CSV with one row per market per day or week: <span className="cc-mono">date, location, {rev ? "sales" : "orders"}</span> (an <span className="cc-mono">adspend</span> column is optional). At least 1 year of history is best.</span>
              <input ref={preInput} type="file" accept=".csv" hidden onChange={e => { const f = e.target.files?.[0]; if (f) uploadHistory(f); e.target.value = ""; }} />
              {busy === "history" ? (
                <div className="cc-file"><Spinner /><span style={{ flex: 1, fontSize: 13 }}>{progress < 100 ? `Uploading… ${progress}%` : "Checking the data…"}</span></div>
              ) : !d.hist ? (
                <div><button className="cc-btn primary" disabled={!!busy || engine.sample} onClick={() => preInput.current?.click()}>Upload CSV</button></div>
              ) : (
                <>
                  <div className="cc-file">
                    <span className="cc-mono" style={{ fontSize: 12.5, flex: 1 }}>
                      {d.hist.file_name} · {d.facts?.markets} markets · {d.facts?.periods} {d.facts?.granularity === "daily" ? "days" : "weeks"} · {d.facts?.date_from} → {d.facts?.date_to}
                    </span>
                    {!engine.sample && <button className="cc-btn" style={{ padding: "6px 12px" }} disabled={!!busy || powerRunning} onClick={() => preInput.current?.click()}>Change file</button>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span className="cc-fl">Data checks (your guardrail rules)</span>
                    <ChecksList checks={forStage("data")} />
                  </div>
                  {sc && (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span className="cc-qh">Select markets</span>
                        <Select mode="multiple" size="large" style={{ width: "100%" }} placeholder="Choose one or more markets"
                          value={scMarkets} options={marketOptions} maxTagCount="responsive" optionFilterProp="label"
                          disabled={powerRunning || engine.sample} onChange={(v: string[]) => update({ scMarkets: v })} />
                        <span className="cc-help">The markets where ads will run. Suggested markets each lead a group of markets with similar sales; the power analysis tests each one against a synthetic control.</span>
                      </div>
                      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 220px", display: "flex", flexDirection: "column", gap: 6 }}>
                          <span className="cc-qh">Campaign start date</span>
                          <DatePicker size="large" format="YYYY-MM-DD" placeholder={`Last ${s.maxWeeks} weeks of history`}
                            value={scStart ? dayjs(scStart) : null} showToday={false} disabled={powerRunning || engine.sample}
                            defaultPickerValue={scPickerOpen}
                            disabledDate={c => !!(d.facts?.date_from && d.facts?.date_to)
                              && (c.isBefore(dayjs(d.facts.date_from), "day") || c.isAfter(dayjs(d.facts.date_to), "day"))}
                            onChange={v => update({ scCampaignStart: v ? snapToHistory(v) : undefined })} style={{ width: "100%" }} />
                          <span className="cc-help">
                            A date in the history. Data before it trains the synthetic controls; the test window runs from it to {d.facts?.date_to}
                            {` (${scWeeks} week${scWeeks === 1 ? "" : "s"}${scWindow != null && scWeeks > s.maxWeeks ? `, longer than the ${s.maxWeeks} weeks in the Plan` : ""}).`}
                          </span>
                        </div>
                        <div style={{ flex: "1 1 220px", display: "flex", flexDirection: "column", gap: 6 }}>
                          <span className="cc-qh">Average monthly BAU media spend</span>
                          <BauInput valueK={bauMonthlyK} onChange={k => update({ rctBauMonthlyK: k })} disabled={powerRunning || engine.sample}
                            ariaLabel="Average monthly business-as-usual media spend across all markets in dollars" />
                          <span className="cc-help">All markets, before the test. The engine sizes each market's investment from it.</span>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span className="cc-qh">Regression type</span>
                        <div role="radiogroup" aria-label="Regression type" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          {([
                            ["Ridge ASCM", "Augmented synthetic control", "Adds a ridge-regression correction when the twin doesn't fit closely.", true],
                            ["SCM", "Synthetic control", "Weights the other markets into a twin of the test market.", false],
                          ] as [RegressionType, string, string, boolean][]).map(([rt, label, desc, isDefault]) => {
                            const on = regressionType === rt;
                            return (
                              <button key={rt} role="radio" aria-checked={on} className={opt(on)} disabled={powerRunning || engine.sample}
                                onClick={() => update({ scRegressionType: rt })} style={{ flex: "1 1 220px", padding: "12px 14px" }}>
                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span aria-hidden="true" style={{
                                    width: 16, height: 16, borderRadius: "50%", flexShrink: 0, boxSizing: "border-box",
                                    border: `${on ? 5 : 1.5}px solid ${on ? "var(--blue)" : "var(--border-default)"}`,
                                  }} />
                                  <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{rt}</span>
                                  {isDefault && <span className="cc-help" style={{ marginLeft: "auto", fontSize: 11, padding: "1px 8px", borderRadius: 999, border: "1px solid var(--border-default)" }}>Default</span>}
                                </span>
                                <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text-secondary)", paddingLeft: 24 }}>{label}</span>
                                <span className="cc-help" style={{ paddingLeft: 24 }}>{desc}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span className="cc-qh">Statistical significance threshold</span>
                        <StatSigInput value={statSig} disabled={powerRunning || engine.sample}
                          onChange={v => { setStatSigBad(v === undefined); if (v !== undefined) update({ scStatSig: v }); }} />
                        <span className="cc-help">The confidence a measured lift needs to count as significant, as in the GeoLift study. Used by Incrementality results and the readout.</span>
                      </div>
                      {pastCampaign && scStart !== pastCampaign.from && !powerRunning && (
                        <div className="cc-note warn" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <span style={{ flex: "1 1 260px" }}>
                            The history has ad spend from {pastCampaign.from} to {pastCampaign.to} ({pastCampaign.periods} {daily ? "days" : "weeks"}), which looks like a past campaign.
                            Starting the test window there keeps those weeks out of the training data.
                          </span>
                          <button className="cc-btn" style={{ padding: "5px 12px" }} disabled={engine.sample} onClick={() => update({ scCampaignStart: pastCampaign.from })}>Use {pastCampaign.from}</button>
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <button className={`cc-btn${!powerRunning && bauMonthlyK && (!d.designs.length || marketsChanged || inputsChanged) ? " primary" : ""}`}
                          disabled={!scMarkets.length || !bauMonthlyK || statSigBad || powerRunning || !!busy || engine.sample} onClick={startPower}>
                          {powerRunning ? "Running…" : d.designs.length ? "Re-run power analysis" : "Run power analysis"}
                        </button>
                        {!bauMonthlyK && <span className="cc-help">Enter the monthly BAU media spend to run.</span>}
                      </div>
                      {(marketsChanged || inputsChanged) && !powerRunning && <div className="cc-note warn">The inputs changed since the last run. Re-run the power analysis to update the results.</div>}
                    </>
                  )}
                </>
              )}
              {engine.history?.status === "failed" && <div className="cc-note bad">{engine.history.error}</div>}
              {sc && powerRunning && (
                <JobProgress job={live?.power} startedAt={d.power?.started_at} label="The GeoLift engine is running a synthetic control for each candidate market. This usually takes a few minutes." />
              )}
              {sc && d.power?.status === "failed" && <div className="cc-note bad">{d.power.error}</div>}
            </div>

            {sc ? (
              <>
                {d.designs.length > 0 && d.hist?.job_config_id && (
                  <CollapseSection key={`power-${d.power?.job_id}`} title="Power analysis results" defaultOpen
                    hint={`${d.designs.length} market${d.designs.length > 1 ? "s" : ""}`}>
                  <div className="cc-geolift">
                    <ViewTestAndControlMarket
                      key={d.power?.job_id || "power"}
                      jobConfigId={d.hist.job_config_id}
                      testMarkets={d.designs.map(r => r.markets[0])}
                      selectedMarket={d.design?.markets[0] || d.designs[0].markets[0]}
                      marketData={[{ ID: d.designs[0].selection_id ?? 1, duration: d.designs[0].duration ?? d.designs[0].weeks } as any]}
                      preCampaignData={{ geoLiftStudyName: s.name, buyType: "national", fileList: [] }}
                      onMarketChange={() => {}}
                      mode="plan"
                      buyType="national"
                      isSales={rev}
                    />
                  </div>
                  </CollapseSection>
                )}
                {d.designs.length > 0 && d.hist?.job_config_id && (
                  <CollapseSection key={`lift-${d.power?.job_id}`} title="Incrementality results"
                    hint={d.power?.campaign_start ? `${d.power.campaign_start} → ${d.facts?.date_to}` : `Last ${d.weeks} weeks of history`}>
                    <span className="cc-help">GeoLift on the test window (campaign start date to the end of the history). If no campaign ran in those weeks, a lift near 0% means the synthetic controls fit well.</span>
                    <div className="cc-geolift">
                      <ViewResults jobConfigId={d.hist.job_config_id} resultData={null} mode="plan" isSales={rev} />
                    </div>
                  </CollapseSection>
                )}
              </>
            ) : (
              <>
              <div className="cc-card">
                <div className="cc-qh"><span>2 · Randomized split &amp; power analysis</span><span className="cc-help">Adrsta RCT engine</span></div>
                {!d.hist ? <span className="cc-help">Upload history first.</span> : (
                  <>
                    <span className="cc-fl">Split (test / control)</span>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {([50, 60, 70, 80] as const).map(sp => (
                        <button key={sp} className={pill(sp === 50)} disabled={sp !== 50 || rctRunning || engine.sample}
                          title={sp === 50 ? undefined : "Coming soon"}>{sp} / {100 - sp}</button>
                      ))}
                    </div>
                    <span className="cc-help">Only 50/50 is available for now.</span>
                    <span className="cc-fl">Candidate test durations</span>
                    <DurationsInput value={rctDurations} onChange={v => update({ rctDurations: v })} disabled={rctRunning || engine.sample} />
                    <span className="cc-help">Add up to 5 durations, each between 4 and 8 weeks. The engine picks the best one.</span>
                    <span className="cc-fl">Weekly incremental spend cap</span>
                    <DollarInput value={rctWeeklyCap} onChange={v => update({ rctWeeklyCap: v })} per="week" placeholder="e.g. 80,000"
                      ariaLabel="Weekly incremental spend cap in dollars" disabled={rctRunning || engine.sample} />
                    <span className="cc-fl">Total weekly business-as-usual ad spend (all markets)</span>
                    <DollarInput value={rctWeeklySpend} onChange={v => update({ rctWeeklySpend: v })} per="week" placeholder="e.g. 1,000,000"
                      ariaLabel="Total weekly business-as-usual ad spend across all markets in dollars" disabled={rctRunning || engine.sample} />
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <button className={`cc-btn${!rctRunning && !d.rctDesign ? " primary" : ""}`}
                        disabled={!rctReady || rctRunning || !!busy || engine.sample} onClick={startRct}>
                        {rctRunning ? "Running…" : d.rctDesign || d.rct?.status === "failed" ? "Re-run RCT design" : "Run RCT design"}
                      </button>
                      <span className="cc-help">{rctReady ? "Thousands of random splits per duration." : "Add at least one duration and both amounts."}</span>
                    </div>
                  </>
                )}
                {rctRunning && (
                  <JobProgress job={live?.rct} startedAt={d.rct?.started_at} label="The RCT engine is searching random splits on your data. This usually takes a few minutes; larger histories take longer." />
                )}
                {d.rct?.status === "failed" && <div className="cc-note bad">{d.rct.error}</div>}
                {d.rctDesign && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div className="cc-grid-4" style={{ gap: 10 }}>
                      <div className="cc-tile"><span className="cc-lbl">Best duration</span><span className="cc-val">{d.rctDesign.weeks} wk</span></div>
                      <div className="cc-tile"><span className="cc-lbl">Test / control</span><span className="cc-val">{d.rctDesign.test_markets} / {d.rctDesign.control_markets}</span></div>
                      <div className="cc-tile"><span className="cc-lbl">MDE</span><span className="cc-val">{rx?.percentageLift != null ? `${num2(rx.percentageLift)}%` : d.mdeText}</span></div>
                      <div className="cc-tile"><span className="cc-lbl">Invest / week</span><span className="cc-val">{rx?.incrementalInvestment != null ? usd(rx.incrementalInvestment / d.rctDesign.weeks) : money(d.rctDesign.weekly_investment_k)}</span></div>
                    </div>
                    <span className="cc-help">
                      Total investment {rx?.incrementalInvestment != null ? usd(rx.incrementalInvestment) : money(d.rctDesign.investment_k)} over {d.rctDesign.weeks} weeks, sized for 80% power.
                      {d.rct?.dropped?.length ? ` Left out: ${d.rct.dropped.join(", ")}.` : ""}
                    </span>
                  </div>
                )}
              </div>
              {d.rctDesign && id && (
                <RctDesignResults testId={id} jobId={d.rct?.job_id} design={d.rctDesign} marketSales={d.rct?.market_sales}
                  onError={msg => t.setError(msg)} />
              )}
              </>
            )}
          </div>

          <div style={{ flex: "1 1 280px", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cc-card">
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>Design guardrails</span>
              <ChecksList checks={forStage("design")} compact />
            </div>
            <button className={`cc-btn${designNextOk ? " primary" : ""}`} disabled={!designNextOk || (sc && marketsChanged)} onClick={() => go(3)}>
              {!d.designDone ? (sc ? "Upload data and run the power analysis" : "Upload data and run the RCT design")
                : designBlocks.length ? `Clear ${designBlocks.length} blocker${designBlocks.length > 1 ? "s" : ""} to continue` : "Continue to Test plan →"}
            </button>
          </div>
        </div>
      )}

      {/* ── 3 · TEST PLAN ── */}
      {s.stage === 3 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 300px", maxWidth: 360, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Success bar</span>
              <span className="cc-fl">{rev ? "Success if iROAS is at least" : "Success if lift is at least"}</span>
              <div className="cc-stepper">
                <button aria-label="Lower" onClick={() => update(p => rev ? { bar: Math.max(0.5, p.bar - 0.5) } : { cntBar: Math.max(1, p.cntBar - 1) })}>−</button>
                <span>{rev ? s.bar.toFixed(1) + "x" : s.cntBar + "%"}</span>
                <button aria-label="Raise" onClick={() => update(p => rev ? { bar: Math.min(5, p.bar + 0.5) } : { cntBar: Math.min(15, p.cntBar + 1) })}>+</button>
              </div>
              <span className="cc-help">{rev ? "iROAS = incremental revenue ÷ ad spend. 2.0x means every $1 brought $2 of extra sales." : "Lift = how much higher the test markets were than expected without ads."}</span>
              <span className="cc-fl">If the bar is met</span>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className={pill(s.onWin === "scale")} onClick={() => update({ onWin: "scale" })}>Increase budget</button>
                <button className={pill(s.onWin === "roll")} onClick={() => update({ onWin: "roll" })}>Roll out to all markets</button>
                <button className={pill(s.onWin === "keep")} onClick={() => update({ onWin: "keep" })}>Keep current spend</button>
              </div>
            </div>
            <button className="cc-btn primary" onClick={() => go(4)}>Approve plan &amp; schedule →</button>
          </div>
          <article ref={planRef} className="cc-paper" style={{ flex: "999 1 440px", minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10.5, letterSpacing: ".1em", color: "#6B7280", fontWeight: 700 }}>TEST PLAN · {d.adv.toUpperCase()}</span>
                <span style={{ fontSize: 12.5, color: "#6B7280" }}>{s.name.trim() || "Geo test"} · prepared {fmtDayYear(parseDay(todayIso()))}</span>
              </div>
              <button className="cc-btn cc-noprint" style={{ background: "#FFFFFF", color: "#374151", borderColor: "#D1D5DB" }} onClick={downloadPlan}>Download PDF</button>
            </div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{question}</h2>
            <div style={{ border: "1.5px solid #2563EB", background: "#EFF6FF", borderRadius: 10, padding: "14px 16px", display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 14 }}>
              <div><div style={{ fontSize: 11.5, fontWeight: 700, color: "#047857" }}>IF {d.barCond}</div><div style={{ fontSize: 13.5, marginTop: 4 }}>{winTexts[s.onWin]}</div></div>
              <div><div style={{ fontSize: 11.5, fontWeight: 700, color: "#B45309" }}>IF NOT</div><div style={{ fontSize: 13.5, marginTop: 4 }}>{d.adv} keeps spend as is and retests with more markets or weeks.</div></div>
            </div>
            <div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Method</span><span>{sc ? "Synthetic control (Adrsta GeoLift)" : `Randomized geo split, ${d.split}/${100 - d.split} (Adrsta RCT)`}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>KPI</span><span>{rev ? "Revenue ($), with iROAS" : "Count (no $ values)"}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Test markets</span><span>{sc ? (d.design ? `${d.design.markets.length}: ${d.design.markets.map(nm).join(", ")}` : "—") : (d.rctDesign ? `${d.rctDesign.test_markets}, chosen at random` : "—")}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Comparison</span><span>{sc
                ? `A synthetic control for each test market, built from the other ${Math.max(0, (d.facts?.markets ?? 0) - d.nMarkets)} markets`
                : d.rctDesign ? `${d.rctDesign.control_markets} control markets, chosen at random (no ads)` : "—"}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Based on</span><span>{d.facts ? `History: ${d.facts.date_from} → ${d.facts.date_to} · ${d.facts.periods} ${d.facts.granularity === "daily" ? "days" : "weeks"} · ${d.facts.markets} markets` : "—"}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Flight</span><span className="cc-mono">{d.flight}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Budget</span><span className="cc-mono">{money(d.wkSpendK)}/wk · {money(d.totalSpendK)} total</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Expected result</span><span>{sc
                ? (d.design ? `${d.design.mde.toFixed(1)}% lift, about ${rev ? dollars(d.design.incremental) : Math.round(d.design.incremental).toLocaleString()} incremental over ${d.weeks} weeks (power ${d.design.power}%)` : "—")
                : (d.rctDesign ? `Detects lifts of ${d.mdeText} or more (power ${d.rctDesign.power}%)` : "—")}</span></div>
              <div className="cc-prow" style={{ borderBottom: "1px solid #E5E7EB" }}><span style={{ color: "#6B7280" }}>Results</span><span>Read mid-flight: in-flight data is uploaded during the {d.weeks}-week flight and {sc ? "GeoLift" : "the RCT model"} measures lift{rev ? ", incremental revenue and iROAS" : " and cost per incremental"} so far. Reads are directional until the flight ends; the last read decides.</span></div>
            </div>
            {sc && planRows.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Power analysis by market</span>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) 1fr 1fr 1.2fr", fontSize: 12.5 }}>
                  {["Market", "Expected lift", "Power", "Incremental"].map(h => (
                    <span key={h} style={{ padding: "6px 0", color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #E5E7EB" }}>{h}</span>
                  ))}
                  {planRows.map(r => (
                    <React.Fragment key={r.markets[0]}>
                      <span style={{ padding: "6px 0", borderBottom: "1px solid #F3F4F6" }}>{nm(r.markets[0])}</span>
                      <span className="cc-mono" style={{ padding: "6px 0", borderBottom: "1px solid #F3F4F6" }}>{r.mde.toFixed(1)}%</span>
                      <span className="cc-mono" style={{ padding: "6px 0", borderBottom: "1px solid #F3F4F6" }}>{r.power}%</span>
                      <span className="cc-mono" style={{ padding: "6px 0", borderBottom: "1px solid #F3F4F6" }}>{rev ? dollars(r.incremental) : Math.round(r.incremental).toLocaleString()}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {!sc && !!d.rct?.market_rows?.length && (
              <div style={{ fontSize: 13, lineHeight: 1.5 }}><b>Largest test markets (spend / week):</b> {d.rct.market_rows.slice(0, 5).map(m => `${m.name} ${money(m.weekly_k)}`).join(", ")}.</div>
            )}
            <div style={{ fontSize: 13, lineHeight: 1.5 }}><b>Rules during the test:</b> ads run only in test markets; {s.change === "yes"
              ? `planned change in test markets: ${s.changeNote || "a promo, price change or other campaign"}`
              : "nothing else changes in test markets"}; in-flight data is shared for mid-flight reads through {fmtDayYear(d.end)}.</div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}><b>Accepted risks:</b> {
              [...forStage("plan"), ...forStage("data"), ...forStage("design")].filter(c => c.status === "fail" && c.level === "guard").map(c => c.detail).join(" ") || "none."
            }</div>
          </article>
        </div>
      )}

      {/* ── 4 · IN FLIGHT: mid-flight reads ── */}
      {s.stage === 4 && (
        <>
          <div className="cc-card">
            <div className="cc-tl">
              {phases.map((p, i) => (
                <div key={p[0]} className={`cc-tl-step${i < s.phase ? " done" : i === s.phase ? " cur" : ""}`}>
                  <span style={{ fontWeight: 600 }}>{p[0]}</span>
                  <span className="cc-mono" style={{ fontSize: 12 }}>{fmtDayYear(p[1])}</span>
                  <span className="cc-help">{p[2]}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button className={`cc-btn${s.phase >= 2 ? "" : " primary"}`} disabled={s.phase >= 2} onClick={() => update(p => ({ phase: Math.min(2, p.phase + 1) }))}>
                {["Jump to start date", "Jump to end date", "Flight ended"][Math.min(2, s.phase)]}
              </button>
              <span className="cc-help">Demo control: jump the calendar forward.</span>
            </div>
          </div>

          {s.phase < 1 ? (
            <div className="cc-note">Mid-flight reads open when the ads start on {fmtDayYear(d.start)}.</div>
          ) : (
            <div className="cc-card">
              <div className="cc-qh"><span>Full-campaign read</span>
                <Badge tone={readoutRunning ? "blue" : !d.measured ? "grey" : d.final ? "green" : "violet"}>
                  {readoutRunning ? "Running" : !d.measured ? "No read yet" : d.final ? "Final read" : `Directional · week ${d.weeksIn} of ${d.weeks}`}
                </Badge></div>
              <span className="cc-help">
                Upload the data so far: same CSV format as the history, from the campaign start to today{rev ? " (add an adspend column to compute iROAS from actual spend)" : ""}.
                Read as often as you like; reads are directional until the flight ends, and the last read decides.
              </span>
              <input ref={postInput} type="file" accept=".csv" hidden onChange={e => { const f = e.target.files?.[0]; if (f) startReadout(f); e.target.value = ""; }} />
              {readoutRunning ? (
                <JobProgress job={live?.readout} startedAt={engine.readout?.started_at}
                  label={sc ? `Running the GeoLift read on ${d.design?.markets.length ?? 0} test markets…` : `Comparing ${d.rctDesign?.test_markets ?? 0} test markets with ${d.rctDesign?.control_markets ?? 0} control markets…`} />
              ) : busy === "readout" ? (
                <div className="cc-file"><Spinner /><span style={{ flex: 1, fontSize: 13 }}>{progress < 100 ? `Uploading… ${progress}%` : "Starting the read…"}</span></div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {d.ro?.file_name && <span className="cc-file" style={{ flex: "1 1 260px" }}><span className="cc-mono" style={{ fontSize: 12.5 }}>{d.ro.file_name}</span></span>}
                  <button className={`cc-btn${d.measured ? "" : " primary"}`} disabled={engine.sample} onClick={() => postInput.current?.click()}>{d.measured ? "Upload newer data" : "Upload CSV"}</button>
                </div>
              )}
              {d.ro?.status === "failed" && <div className="cc-note bad">{d.ro.error}</div>}
            </div>
          )}

          {d.measured && (
            <>
              {!d.final && (
                <div className={`cc-note${d.tracking ? "" : " warn"}`}>
                  Directional read{d.ro?.as_of ? ` as of ${d.ro.as_of}` : ""} ({d.weeksIn} of {d.weeks} weeks):{" "}
                  {d.tracking ? "on track for the success bar" : "behind the success bar"} so far
                  {d.sig ? `, already at ${d.conf.toFixed(0)}% confidence` : `, confidence ${d.conf.toFixed(0)}% so far`}. The result is decided on the final read.
                </div>
              )}
              <div className="cc-grid-4">
                <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">Lift so far</span><span className="cc-val" style={{ color: d.lift >= 0 ? "var(--success-card)" : "var(--error)" }}>{d.liftText}</span><span className="cc-help">test vs {sc ? "synthetic twin" : "control markets"}</span></div>
                <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">{rev ? "Incremental revenue" : "Incremental"}</span><span className="cc-val">{rev ? dollars(d.incremental) : Math.round(d.incremental).toLocaleString()}</span><span className="cc-help">caused by the ads so far</span></div>
                <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">{d.effLabel}</span><span className="cc-val">{d.effText}</span><span className="cc-help">{rev ? `on ${money(d.spendK)} spend${d.ro?.spend_source === "plan" ? " (planned to date)" : ""}` : "spend ÷ extra conversions"}</span></div>
                <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">Confidence</span><span className="cc-val" style={{ color: d.sig ? "var(--success-card)" : "var(--warning)" }}>{d.conf.toFixed(0)}%</span><span className="cc-help">{d.ro ? `p-value ${d.ro.pvalue}` : `needs ${d.sigThreshold}% or more`}</span></div>
              </div>
              {d.reads.length > 1 && (
                <div className="cc-card">
                  <span style={{ fontWeight: 600 }}>Reads so far</span>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr>{["As of", "Weeks", "Lift", rev ? "iROAS" : "Incremental", "Confidence", "Read"].map(h => <th key={h} style={{ textAlign: "left", padding: "6px 8px", fontSize: 12, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-subtle)" }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {d.reads.map(r => (
                          <tr key={r.job_id}>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{r.as_of || "—"}</td>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{r.weeks_in != null ? `${r.weeks_in} of ${r.planned_weeks ?? d.weeks}` : "—"}</td>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{`${r.lift >= 0 ? "+" : ""}${r.lift.toFixed(1)}%`}</td>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{rev ? (r.iroas != null ? `${r.iroas.toFixed(2)}x` : "—") : Math.round(r.incremental).toLocaleString()}</td>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{r.confidence.toFixed(0)}%</td>
                            <td style={{ padding: "6px 8px" }}>{r.final === false ? "Directional" : "Final"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {hasChart && chart && (
                <div className="cc-card">
                  <span style={{ fontWeight: 600 }}>{sc ? "Test markets vs their synthetic twin (GeoLift)" : "Test markets vs the prediction from control markets"}</span>
                  <ReadoutChart t={chart.t!} c={chart.c!} startIndex={chart.start_index ?? chart.t!.length - 1} dates={chart.dates}
                    testLabel="Test markets (total)"
                    ctrlLabel={sc ? "Synthetic twin (no ads)" : "Predicted from control markets (no ads)"} money={rev} />
                </div>
              )}
              <div className="cc-card">
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>Results guardrails</span>
                <ChecksList checks={forStage("results")} />
              </div>
            </>
          )}
          {/* Decide opens when the flight ends; a result that misses a rule (e.g. low confidence) is still recorded there. */}
          <button className={`cc-btn${s.phase >= 2 && d.measured ? " primary" : ""}`} style={{ alignSelf: "flex-start" }} disabled={s.phase < 2 || !d.measured} onClick={() => go(5)}>
            {s.phase < 2 ? `Decide opens when the flight ends (${fmtDayYear(d.end)})` : !d.measured ? "Run a read to decide" : "Compare with the plan →"}
          </button>
        </>
      )}

      {/* ── 5 · DECIDE ── */}
      {s.stage === 5 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <article className="cc-paper" style={{ flex: "999 1 480px", minWidth: 0 }}>
            <span style={{ fontSize: 10.5, letterSpacing: ".1em", color: "#6B7280", fontWeight: 700 }}>RESULT VS PLAN</span>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{question}</h2>
            <div style={{ borderRadius: 10, padding: "14px 16px", fontWeight: 600, ...(d.met ? { background: "#ECFDF5", border: "1.5px solid #047857", color: "#065F46" } : { background: "#FFFBEB", border: "1.5px solid #B45309", color: "#92400E" }) }}>
              {d.met ? "Yes: the success bar was met." : d.sig ? "No: there was a measurable effect, but it missed the bar." : `Not significant: confidence ${d.conf.toFixed(0)}% is below ${d.sigThreshold}%.`}
            </div>
            <div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr", fontWeight: 600, color: "#6B7280", fontSize: 12 }}><span>Metric</span><span>Planned</span><span>Actual</span></div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr" }}><span style={{ color: "#6B7280" }}>Success bar</span><span className="cc-mono">{d.barCond}</span><span className="cc-mono">{rev ? `iROAS ${d.iroas.toFixed(2)}x` : `lift ${d.lift.toFixed(1)}%`}{d.sig ? "" : " (not significant)"}</span></div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr" }}><span style={{ color: "#6B7280" }}>Lift</span><span className="cc-mono">≥ {d.mdeText} detectable</span><span className="cc-mono">{d.liftText}</span></div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr" }}><span style={{ color: "#6B7280" }}>Confidence</span><span className="cc-mono">{d.sigThreshold}%</span><span className="cc-mono">{d.conf.toFixed(0)}%</span></div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr", borderBottom: "1px solid #E5E7EB" }}><span style={{ color: "#6B7280" }}>Spend</span><span className="cc-mono">{money(d.totalSpendK)}</span><span className="cc-mono">{money(d.spendK)}</span></div>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: "#4B5563" }}>Applies to {d.flight}, these markets and this budget. Retest before applying it to another season or a national rollout.</div>
          </article>
          <div style={{ flex: "1 1 280px", maxWidth: 340, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Record the decision</span>
              <span className="cc-help">{d.met ? `The plan said: ${winTexts[s.onWin]}` : "Tip: retest with a higher weekly budget or more weeks; the design step will then offer stronger designs."}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {decDefs.map(([key, label]) => (
                  <button key={key} className={`cc-combo${s.finalDecision === key ? " on" : ""}`} disabled={s.saved} onClick={() => update({ finalDecision: key })}>
                    <span className={`cc-radio${s.finalDecision === key ? " on" : ""}`} /><span style={{ flex: 1 }}>{label}</span>
                  </button>
                ))}
              </div>
              <button className={`cc-btn${s.saved ? " done" : s.finalDecision ? " primary" : ""}`} disabled={!s.finalDecision || s.saved}
                onClick={() => update(p => ({ saved: true, result: mlResult({ ...p, saved: true }, engine) }))}>
                {s.saved ? "Saved ✓" : "Save decision & close test"}
              </button>
            </div>
            {s.saved && <div className="cc-note ok">Saved. The test is closed and added to the Learning library as a benchmark.</div>}
            {s.saved && <button className="cc-btn" onClick={() => navigate(`${BASE}/library`)}>Open Learning library →</button>}
            <button className="cc-btn" onClick={() => navigate(`${BASE}/new`)}>Run another new test</button>
          </div>
        </div>
      )}
    </div>
  );
}

